import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setTokenState] = useState(() => localStorage.getItem('srs_token'));

  const setToken = useCallback((t) => {
    if (t) {
      localStorage.setItem('srs_token', t);
    } else {
      localStorage.removeItem('srs_token');
    }
    setTokenState(t);
  }, []);

  const api = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    const t = localStorage.getItem('srs_token');
    if (t) instance.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    return instance;
  }, []);

  useEffect(() => {
    async function check() {
      const t = localStorage.getItem('srs_token');
      if (!t) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${t}` }
        });
        setUser(data);
      } catch {
        localStorage.removeItem('srs_token');
        setTokenState(null);
      }
      setLoading(false);
    }
    check();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      setToken(data.token);
      setUser({ ...data.user, onboarding_required: data.onboarding_required });
      return { success: true, onboarding_required: data.onboarding_required };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password, clinicName) => {
    try {
      const { data } = await axios.post(`${API}/api/auth/register`, {
        name, email, password, clinic_name: clinicName
      });
      setToken(data.token);
      setUser({ ...data.user, onboarding_required: true });
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const t = localStorage.getItem('srs_token');
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setUser(data);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
